import { gql, TypedDocumentNode } from '@apollo/client';
import { PaymentSource } from '../../../constants';
import { CART_FRAGMENT, CartVersion, Cart } from '../cart.rest';

export enum CartUpdateAction {
  Gift = 'promotion',
  Reward = 'mo_rewards',
  ShippingAddress = 'address',
  ShippingMethod = 'shipping_packaging',
  PaymentMethod = 'payment_method',
  PaymentSource = 'payment_source',
  SiteCredit = 'site_credit',
  Gifting = 'gifting',
  UpdateCart = 'update_cart',
  AddGiftCard = 'add_gift_card',
  RemoveGiftCard = 'remove_gift_card'
}

export enum CartUpdatePropertyName {
  Gift = 'promotion_code',
  Reward = 'use_loyalty_rewards',
  CountryCode = 'country_code',
  ShippingAddress = 'address_id',
  ShippingMethod = 'shipment_method',
  PaymentMethod = 'payment_id',
  PaymentSource = 'payment_source',
  SiteCredit = 'site_credit_amount',
  SiteCreditAutoApply = 'auto_apply',
  Gifting = 'is_gift_order',
  GiftingMessage = 'gift_notes',
  RenderedBy = 'rendered_by',
  Phone = 'phone',
  AddressLine1 = 'address_line1',
  AddressLine2 = 'address_line2',
  FirstName = 'first_name',
  LastName = 'last_name',
  Default = 'default',
  State = 'state',
  City = 'city',
  PostalCode = 'postal_code',
  GiftCard = 'gift_card_number',
  GiftCardPin = 'gift_card_pin'
}

type RestUserCartsAttributes = {
  state: string | undefined;
  city: string | undefined;
  phone: string;
  postal_code: string | undefined;
  country_code: string | undefined;
  address_line1: string;
  address_line2: string;
  first_name: string;
  last_name: string;
  default: boolean;
};

export type UpdateCartMutationAttributes = Partial<{
  id: string;
  address: RestUserCartsAttributes;
  [CartUpdatePropertyName.ShippingAddress]: string;
  [CartUpdatePropertyName.Gift]: string | null;
  [CartUpdatePropertyName.Reward]: number | null;
  [CartUpdatePropertyName.CountryCode]: string | null;
  [CartUpdatePropertyName.ShippingMethod]: number;
  [CartUpdatePropertyName.PaymentMethod]: string | null;
  [CartUpdatePropertyName.PaymentSource]: PaymentSource;
  [CartUpdatePropertyName.SiteCredit]: number;
  [CartUpdatePropertyName.SiteCreditAutoApply]: boolean;
  [CartUpdatePropertyName.Gifting]: boolean | null;
  [CartUpdatePropertyName.GiftingMessage]: string | null;
  [CartUpdatePropertyName.RenderedBy]: string | null;
  [CartUpdatePropertyName.AddressLine1]: string;
  [CartUpdatePropertyName.AddressLine2]: string;
  [CartUpdatePropertyName.FirstName]: string;
  [CartUpdatePropertyName.LastName]: string;
  [CartUpdatePropertyName.Default]: boolean;
  [CartUpdatePropertyName.Phone]: string;
  [CartUpdatePropertyName.State]: string | null;
  [CartUpdatePropertyName.City]: string | null;
  [CartUpdatePropertyName.PostalCode]: string | null;
  [CartUpdatePropertyName.GiftCard]: string | null;
  [CartUpdatePropertyName.GiftCardPin]: number | null;
}>;

type UpdateCartMutationVariablesInputData = {
  action_type: CartUpdateAction;
  attributes: UpdateCartMutationAttributes;
  id: string;
  type: 'carts';
};

type UpdateCartMutationVariablesInput = {
  data: UpdateCartMutationVariablesInputData;
};

export type UpdateCartMutation = {
  updateCart: {
    data: Cart[];
  };
};

export type UpdateCartMutationVariables = {
  id: string;
  countryCode: string;
  target: CartVersion;
  input: UpdateCartMutationVariablesInput;
};

export const UPDATE_CART_MUTATION: TypedDocumentNode<
  UpdateCartMutation,
  UpdateCartMutationVariables
> = gql`
  mutation UpdateCartMutation(
    $id: String!
    $countryCode: String!
    $target: String!
    $input: UpdateCartMutationVariablesInput!
  ) {
    updateCart(id: $id, countryCode: $countryCode, target: $target, input: $input)
      @rest(
        type: "RestUserCart"
        endpoint: "moda"
        path: "/v3.5/cart/{args.id}?target={args.target}&country_code={args.countryCode}"
        method: "PATCH"
      ) {
      data @type(name: "RestUserCartData") {
        ...CartFragment
      }
    }
  }
  ${CART_FRAGMENT}
`;
